'use client'

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiFetch } from '@/lib/api-fetch'
import { queryKeys } from '../keys'
import type { QuickVideoSettingsDTO } from '@/types/quick-video'

async function jsonOrThrow<T>(res: Response): Promise<T> {
  const data = await res.json().catch(() => ({}))
  if (!res.ok) {
    const message = typeof data?.message === 'string'
      ? data.message
      : typeof data?.error === 'string'
        ? data.error
        : 'Request failed'
    throw new Error(message)
  }
  return data as T
}

export function useQuickVideoSettings(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: queryKeys.quickVideo.settings(),
    enabled: options.enabled ?? true,
    staleTime: 60_000,
    queryFn: async (): Promise<{ settings: QuickVideoSettingsDTO }> => {
      const res = await apiFetch('/api/quick-video/settings')
      return jsonOrThrow<{ settings: QuickVideoSettingsDTO }>(res)
    },
  })
}

export function useUpdateQuickVideoSettings() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: Partial<QuickVideoSettingsDTO>) => jsonOrThrow<{ settings: QuickVideoSettingsDTO }>(
      await apiFetch('/api/quick-video/settings', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }),
    ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.quickVideo.settings() }),
  })
}
